/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { FormInfoTourney } from './components';

import { Button, Loader, Modal } from '@/components';
import { Layout, TourneyList } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  requestExpelMemberFCup,
  requestPostulantManagementFCup,
  ActionTypeManageFCupEnum,
  requestFCupViewTeam,
  inviteUserById,
} from '@/services/friendsCup';
import { FCupParticipants, FCupViewTeamResponse } from '@/types/friendCup';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import { GENERICS_WORDS, TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const initialState = {
  showModalRequest: false,
  showModalFullTeam: false,
  showModalExpel: false,
  nameUser: '',
  idUser: 0,
};

export const ViewAdminFCup: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const toast = useToast();
  const { tourneyData, handleSetTourney } = useTourney();
  const { userInitialData } = useAuth();
  const { isMobile, isDesktop } = useIsDesktop();
  const [state, setState] = useState(initialState);

  const { mutateAsync: sendInvitation, isLoading: loadingInvitation } =
    useMutation(
      async (userId: number) => await inviteUserById(tourneyData.id, userId),
      {
        onSuccess: (response) => {
          if (response.estado) {
            toast.show('La invitación fue enviada exitosamente.');
            refetch();
          } else if (response.invitacionConvertidaEnAceptacion) {
            // acepto al usuario postulado anteriormente automaticamente
            refetch();
          } else {
            toast.show(
              response.mensaje ?? 'Hubo un problema al enviar la invitación.'
            );
          }
        },
        onError: () => {
          toast.show(
            'Ocurrió un error al enviar la invitación. Por favor, intenta de nuevo.'
          );
        },
      }
    );

  const { refetch, isRefetching } = useQuery(
    'FCupViewTeam',
    async () => await requestFCupViewTeam(tourneyData.id),
    {
      onSuccess: async (response: FCupViewTeamResponse) => {
        if (response.estado) {
          handleSetTourney({
            ...tourneyData,
            tourney: response,
          });
          if (response?.fechasCmbVerEquipo2) {
            setState((prevState) => ({
              ...prevState,
              showModalFullTeam: true,
            }));
          }
        } else {
          console.error(
            `Error obteniendo datos de ${GENERICS_WORDS.friendsCup}.`
          );
        }
      },
      onError: (error) => {
        console.error(
          `Error obteniendo datos de ${GENERICS_WORDS.friendsCup}.`,
          error
        );
        throw new Error('Invalid response state');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const responseTeam: FCupViewTeamResponse = useMemo(() => {
    return tourneyData.tourney;
  }, [tourneyData]);

  const memberList = useMemo((): GenericItemTourneysData[] | null => {
    if (!responseTeam.participantesGrupo) return null;
    return (
      responseTeam.participantesGrupo.map((item: FCupParticipants) => {
        return {
          id: item.id,
          title: item.nombreYApellido,
          action: {
            txtAction: 'Expulsar',
            variantButton: 'danger',
            txtActionDisabled: 'Expulsar',
          },
          disabled: userInitialData?.idUsuarioDT === item.idUsuario,
          subtitle: isMobile
            ? item.nombreEquipo.toLocaleUpperCase()
            : item.nombreEquipo,
          ...(isMobile && {
            secundaryText: item.provincia || undefined,
          }), // TODO: falta servicio pcia.
        };
      }) || []
    );
  }, [responseTeam.participantesGrupo, isMobile, userInitialData]);

  const invitationList = useMemo((): GenericItemTourneysData[] | null => {
    const list = responseTeam.pendientesAceptarPorInvitado;
    if (!list || list.length === 0) return null;
    return list.map((item: FCupParticipants) => {
      return {
        id: item.idUsuario,
        action: {
          txtAction: 'Reenviar',
          txtActionDisabled: 'Reenviar',
        },
        title: item.nombreYApellido,
        subtitle: item.nombreEquipo,
        disabled: item?.idEstado === 5,
      };
    });
  }, [responseTeam.pendientesAceptarPorInvitado]);

  const requestRecivedList = useMemo((): GenericItemTourneysData[] | null => {
    if (!responseTeam.queAunNoAceptaste) return null;
    return (
      responseTeam.queAunNoAceptaste.map((item: FCupParticipants) => {
        return {
          id: item.id,
          title: item.nombreYApellido,
          subtitle: isMobile
            ? item.nombreEquipo.toLocaleUpperCase()
            : item.nombreEquipo,
          ...(isMobile && {
            secundaryText: item?.provincia || undefined,
          }), // TODO: falta servicio pcia.
        };
      }) || []
    );
  }, [responseTeam.queAunNoAceptaste, isMobile]);

  // VER MODAL EXPULSAR MIEMBRO
  const onHandleMembers = useCallback(
    async (action: ActionsTourneys) => {
      const dataUser = memberList?.find((user) => user.id === action.id);
      setState((prevState) => ({
        ...prevState,
        showModalExpel: true,
        nameUser: `${dataUser?.title}`,
        idUser: dataUser?.id || 0,
      }));
    },
    [memberList]
  );

  // TODO: EXPULSAR MIEMBROS TORNEO
  const onHandleExpelMembers = async (idUser: number) => {
    try {
      const responseExpel = await requestExpelMemberFCup({
        idGrupo: tourneyData.id,
        idParticipante: idUser,
      });

      if (!responseExpel.estado) {
        console.error(responseExpel.mensaje);
        toast.show(responseExpel.mensaje || TEXTS.defaultError);
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    }

    setState(initialState);
    // refetch after action
    refetch();
  };

  //  TRATAR ACEPTAR/RECHAZAR SOLICITUDES RECIBIDAS
  const onHandleRequestActions = useCallback(
    async (actionType: ActionTypeManageFCupEnum, idUser: number) => {
      try {
        const responseManagePostulant = await requestPostulantManagementFCup({
          tipoAccion: actionType,
          idGrupo: tourneyData.id,
          idParticipante: idUser,
          idUsuario: idUser,
        });

        if (responseManagePostulant.estado) {
          // eslint-disable-next-line no-console
          console.log(
            responseManagePostulant.estado,
            'responseManagePostulant'
          );
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show(responseManagePostulant.mensaje || TEXTS.defaultError);
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }

      setState(initialState);
      // refetch after action
      refetch();
    },
    [refetch, toast, tourneyData.id]
  );

  // VER MODAL SOLICITUD RECIBIDA
  const onHandleRequestRecived = async (params: ActionsTourneys) => {
    const dataUser = requestRecivedList?.find((user) => user.id === params.id);

    if (!dataUser?.id) return;

    if (isMobile) {
      setState((prevState) => ({
        ...prevState,
        showModalRequest: true,
        nameUser: `${dataUser?.title}`,
        idUser: dataUser?.id || 0,
      }));
    } else {
      if (params.action === 'reject') {
        onHandleRequestActions(
          ActionTypeManageFCupEnum.ACC_RECHAZAR_POSTULACION,
          dataUser.id
        );
      } else {
        onHandleRequestActions(
          ActionTypeManageFCupEnum.ACC_ACEPTAR_POSTULACION,
          dataUser.id
        );
      }
    }
  };

  const RequestRecivedListUI = useCallback(() => {
    return (
      requestRecivedList && (
        <TourneyList
          action={{
            txtAction: 'Ver solicitud',
            variantButton: 'secondary',
          }}
          className="lg:mt-9 mt-6"
          classPaginator="paginator-sidebar"
          context={EnumContextTourneys.Sidebar}
          handleSubmit={(params) => {
            if (isMobile) {
              params.id && onHandleRequestRecived(params);
            } else {
              params.action && onHandleRequestRecived(params);
            }
          }}
          list={requestRecivedList}
          paginator={isDesktop}
          title={
            requestRecivedList.length > 0 ? (
              <p className="px-4 lg:px-0">Solicitudes</p>
            ) : (
              <></>
            )
          }
          variant={isMobile ? 'default' : 'two-actions'}
        />
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, requestRecivedList]);

  const ButtonInvitation = useCallback(() => {
    return (
      <div className="text-center lg:text-right">
        <Button
          className="font-semibold underline-offset-4 lg:mt-0 mt-6 lg:w-[203px]"
          type="button"
          variant={isMobile ? 'text' : 'secondary'}
          onClick={() => navigate(ROUTES.friendsCup.invite)}
        >
          Invitar amigos
        </Button>
      </div>
    );
  }, [isMobile, navigate]);

  const onHandleGoTeam = useCallback(() => {
    setState((prevState) => ({ ...prevState, showModalFullTeam: false }));
    navigate(ROUTES.friendsCup.viewTeam);
  }, [navigate]);

  return (
    <>
      <Loader isVisible={!responseTeam || isRefetching || loadingInvitation} />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.copadeamigos}
        backButton={isDesktop && true}
        main={() =>
          responseTeam && (
            <>
              {isDesktop && (
                <div>
                  <h1 className="text-2xl text-black font-bold">
                    Administrar Equipo
                  </h1>
                  {/* // TODO: falta servicio de nombre del dueño */}
                  <p className="text-sm text-gdt-gray-4 font-semibold mt-4 mb-">
                    Admin: {userInitialData?.nombre_dt} -{' '}
                    {responseTeam?.cantidadParticipantes !== 0 &&
                      (responseTeam?.cantidadParticipantes !== 1
                        ? responseTeam?.cantidadParticipantes + ' PARTICIPANTES'
                        : responseTeam?.cantidadParticipantes +
                          ' PARTICIPANTE')}
                  </p>
                </div>
              )}
              {/* FORMULARIO DE TORNEO */}
              <div className="lg:max-w-[332px]">
                <FormInfoTourney />
              </div>

              {isMobile && <ButtonInvitation />}

              <div className="lg:max-w-[556px]">
                {/* SOLICITUDES */}
                {isMobile && <RequestRecivedListUI />}

                {/* MIEMBROS TORNEO */}
                {memberList && (
                  <TourneyList
                    key={memberList.length}
                    className="lg:mt-9 mt-6"
                    context={EnumContextTourneys.Admin}
                    handleSubmit={(action) => {
                      action.id && onHandleMembers(action);
                    }}
                    list={memberList}
                    paginator={isDesktop}
                    title={<p className="px-4 lg:px-0">Miembros</p>}
                    variant="default"
                  />
                )}

                {/* TODO: DT's Invitados */}
                {/* DT's Invitados */}
                {invitationList && (
                  <TourneyList
                    key={`${invitationList.length}-invitation-list`}
                    className="lg:mt-9 mt-6"
                    context={EnumContextTourneys.Admin}
                    handleSubmit={(action) => {
                      action.id && sendInvitation(action.id);
                    }}
                    list={invitationList}
                    paginator={isDesktop}
                    title={<p className="px-4 lg:px-0">DT&apos;s invitados</p>}
                    variant="default"
                  />
                )}
              </div>
              {/* MODAL ACEPTACION ACEPTACION/RECHAZO INVITACION RECIBIDAS */}
              <Modal
                controlledOnClose={() =>
                  setState({ ...state, showModalRequest: false })
                }
                isControlledOpen={state.showModalRequest}
                render={() => {
                  return (
                    <div className="text-center mt-2">
                      <p className="text-base font-medium">
                        <strong>{state.nameUser}</strong> quiere unirse a tu
                        <br />
                        Equipo
                      </p>
                      <div className="flex space-x-2 my-10">
                        <Button
                          className="flex-1"
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            onHandleRequestActions(
                              ActionTypeManageFCupEnum.ACC_RECHAZAR_POSTULACION,
                              state.idUser
                            )
                          }
                        >
                          Rechazar
                        </Button>
                        <Button
                          className="flex-1"
                          type="button"
                          variant="primary"
                          onClick={() =>
                            onHandleRequestActions(
                              ActionTypeManageFCupEnum.ACC_ACEPTAR_POSTULACION,
                              state.idUser
                            )
                          }
                        >
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  );
                }}
                variant="controlled"
              />

              <Modal
                controlledOnClose={() =>
                  setState({ ...state, showModalExpel: false })
                }
                isControlledOpen={state.showModalExpel}
                render={() => {
                  return (
                    <div className="text-center mt-2">
                      <p className="text-base font-medium">
                        ¿Estás seguro que deseás EXPULSARLO?
                      </p>
                      <div className="flex space-x-2 my-10">
                        <Button
                          className="flex-1"
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            setState({ ...state, showModalExpel: false })
                          }
                        >
                          Cancelar
                        </Button>
                        <Button
                          className="flex-1"
                          type="button"
                          variant="primary"
                          onClick={() => onHandleExpelMembers(state.idUser)}
                        >
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  );
                }}
                size="xs"
                variant="controlled"
              />
              <Modal
                controlledOnClose={onHandleGoTeam}
                isControlledOpen={state.showModalFullTeam}
                render={() => {
                  return (
                    <div className="text-center mt-2">
                      <p className="text-xl font-bold text-black">
                        Tu equipo está completo
                      </p>
                      <div className="text-center mt-10">
                        <Button
                          className="w-56"
                          type="button"
                          variant="primary"
                          onClick={onHandleGoTeam}
                        >
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  );
                }}
                size="xs"
                variant="controlled"
              />
            </>
          )
        }
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.friendsCup.viewTeam);
            },
          },
          centerContent: {
            title: 'Administrar Equipo',
          },
        }}
        sidebar={() =>
          isDesktop && (
            <>
              {/* botton invitacion  */}
              <ButtonInvitation />
              {/* SOLICITUDES */}
              {<RequestRecivedListUI />}
            </>
          )
        }
        sidebarClassName="lg:w-[427px]"
        withBanners={isDesktop}
      />
    </>
  );
});

ViewAdminFCup.displayName = 'ViewAdminFCup';
